import type {AccountMessages} from "../../i18n/en/account";
import {isBrandColor} from "./brandColors";

/**
 * Pure brand-draft validation (api#98, epic #26 "logic outside React"): the preconditions the brand
 * settings sheet checks before it spends a round-trip.
 *
 * These rules MIRROR the server's, they do not replace it — the api re-validates every write, sniffs the
 * logo's magic bytes (a `File.type` is just a filename guess the browser made) and is the only thing that
 * can reject a payload for real. What the client buys is an instant, translated message instead of a 400.
 *
 * The verdict carries an error KEY, never translated copy: the catalog lookup
 * (`m.account.brand.errors[key]`) stays in the React layer.
 */

/**
 * The image types the api will store. SVG is deliberately absent and must stay absent — it is scriptable
 * XML served back from our own origin, so the api rejects it outright.
 */
export const ACCEPTED_LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

/** The `accept` attribute for the file picker, derived so the two can never disagree. */
export const LOGO_ACCEPT_ATTRIBUTE = ACCEPTED_LOGO_TYPES.join(",");

/**
 * A key into the `account.brand.errors` catalog namespace. Derived from the English catalog so renaming
 * or dropping a message is a compile error here rather than a runtime `undefined`.
 */
export type BrandErrorKey = keyof AccountMessages["account"]["brand"]["errors"];

/**
 * The logo fields the rules read. Structural, so tests need no real `File`.
 *
 * Size is deliberately absent: since ui#601 an oversized logo is COMPRESSED rather than refused, so
 * there is no byte rule left for a sender to fail. `LOGO_IMAGE_TARGET` is what the api's ceiling is
 * expressed as now.
 */
export type BrandLogoDraft = {
	type: string;
};

export type BrandVerdict =
	| {valid: true}
	| {valid: false; error: BrandErrorKey};

/** Validates an accent colour. `null` is valid — it means "clear the colour, keep the logo". */
export function validateBrandColor(color: string | null): BrandVerdict {
	if (color === null) {
		return {valid: true};
	}
	return isBrandColor(color) ? {valid: true} : {valid: false, error: "colorInvalid"};
}

/**
 * Validates a picked logo file — which since ui#601 means its TYPE and nothing else. A logo too big for
 * the api is compressed on the way out (`core/images/compressPickedImage.ts`), so the only thing a
 * sender can still get wrong here is picking something that is not one of the three stored formats.
 */
export function validateBrandLogo(logo: BrandLogoDraft): BrandVerdict {
	if (!ACCEPTED_LOGO_TYPES.includes(logo.type as (typeof ACCEPTED_LOGO_TYPES)[number])) {
		return {valid: false, error: "logoUnsupported"};
	}
	return {valid: true};
}
